/**
 * REVIEW ACTIONS COMPONENT
 *
 * Approve or reject organization with notes
 */

'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Field,
  FieldLabel,
  FieldSet,
  FieldDescription,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle } from 'lucide-react';
import type { Organization } from '@/types/database';
import { updateOrganizationStatus } from '@/lib/onboarding-status';

interface ReviewActionsProps {
  organization: Organization & {
    locations?: Array<{
      id: string;
      name: string;
      description?: string;
      logo_url?: string | null;
    }>;
  };
}

export function ReviewActions({ organization }: ReviewActionsProps) {
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const supabase = createClient();

  const handleApprovePayment = async () => {
    try {
      setPaymentLoading(true);
      setError(null);

      // Update onboarding_queue status to APPROVED
      const { error: updateError } = await supabase
        .from('onboarding_queue')
        .update({
          status: 'APPROVED',
          review_notes: approveNotes || null,
        })
        .eq('id', organization.id);

      if (updateError) {
        console.error('Payment approval update error:', updateError);
        throw updateError;
      }

      // Update onboarding status via edge function
      try {
        await updateOrganizationStatus(organization.id, 'AWAITING_PAYMENT');
      } catch (statusError) {
        console.error('Failed to update onboarding status:', statusError);
        // Continue with checkout even if status update fails
      }

      navigate('/review');

      // // Create Stripe payment checkout session via edge function
      // const response = await fetch(
      //   `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
      //   {
      //     method: 'POST',
      //     headers: {
      //       'Content-Type': 'application/json',
      //       Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      //     },
      //     body: JSON.stringify({
      //       organizationId: organization.id,
      //     }),
      //   }
      // );

      // if (!response.ok) {
      //   const errorData = await response.json();
      //   throw new Error(errorData.error || 'Failed to create payment link');
      // }

      // const { checkoutUrl } = await response.json();

      // // Redirect to Stripe checkout
      // if (checkoutUrl) {
      //   window.location.href = checkoutUrl;
      // } else {
      //   throw new Error('No checkout URL received');
      // }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate payment'
      );
      setPaymentLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setRejectLoading(true);
      setError(null);

      // Update onboarding_queue status (organizations don't get created if rejected)
      const { error: updateError } = await supabase
        .from('onboarding_queue')
        .update({
          status: 'REJECTED',
          rejection_reason: rejectReason,
        })
        .eq('id', organization.id);

      if (updateError) {
        console.error('Rejection update error:', updateError);
        throw updateError;
      }

      // Update onboarding status via edge function
      try {
        await updateOrganizationStatus(organization.id, 'REJECTED');
      } catch (statusError) {
        console.error('Failed to update onboarding status:', statusError);
        // Continue redirect even if status update fails
      }

      navigate('/review');
    } catch (err) {
      console.error('Reject error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject');
      setRejectLoading(false);
    }
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
      {/* Approve */}
      <Card className='border-green-200 dark:border-green-800'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-green-700 dark:text-green-400'>
            <CheckCircle className='w-5 h-5' />
            Approve Organization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FieldSet className='space-y-4'>
            <Field>
              <FieldLabel>Review Notes (Optional)</FieldLabel>
              <Input
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder='Add any notes about this approval...'
                disabled={paymentLoading}
              />
              <FieldDescription>
                These notes will be saved for internal reference
              </FieldDescription>
            </Field>

            {error && !rejectLoading && (
              <div className='text-sm text-red-600 dark:text-red-400'>
                {error}
              </div>
            )}

            <div className='space-y-3'>
              <Button
                onClick={handleApprovePayment}
                disabled={paymentLoading || rejectLoading}
                className='w-full bg-green-600 hover:bg-green-700'
              >
                {paymentLoading
                  ? 'Generating Payment...'
                  : 'Approve & Generate Payment'}
              </Button>
            </div>
          </FieldSet>
        </CardContent>
      </Card>

      {/* Reject */}
      <Card className='border-red-200 dark:border-red-800'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-red-700 dark:text-red-400'>
            <XCircle className='w-5 h-5' />
            Reject Organization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FieldSet className='space-y-4'>
            <Field>
              <FieldLabel>Rejection Reason (Required)</FieldLabel>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder='Provide a reason for rejection...'
                disabled={rejectLoading}
                required
              />
              <FieldDescription>
                This reason will be sent to the client
              </FieldDescription>
            </Field>

            {error && (
              <div className='text-sm text-red-600 dark:text-red-400'>
                {error}
              </div>
            )}

            <Button
              onClick={handleReject}
              disabled={!rejectReason.trim() || rejectLoading}
              variant='destructive'
              className='w-full'
            >
              {rejectLoading ? 'Rejecting...' : 'Reject Organization'}
            </Button>
          </FieldSet>
        </CardContent>
      </Card>
    </div>
  );
}
